import type * as React from 'react';

import type * as api from '@gitbook/api-client';
import * as analytics from '@gitbook/app-analytics/client';
import type { NavigationScreenImport } from '@gitbook/app-navigation';
import { toast } from '@gitbook/spine-react';

import { ImportContentDialog } from '../../Components';
import { NavigationRedirect, useNavigation, useSetAnalyticsScreen } from '../../navigation';
import { apiClient } from '../../state';

/**
 * Prompt to import content as a new space.
 */
export function ImportScreen(props: {
    screen: NavigationScreenImport;
    isOverlay: boolean;
}): React.ReactElement {
    const { screen, isOverlay } = props;
    const { navigate } = useNavigation();

    useSetAnalyticsScreen(
        isOverlay
            ? analytics.createOrgEvent(screen.organization, {
                  screen: 'import',
              })
            : null,
        []
    );

    // This modal should always be rendered as an overlay
    if (!isOverlay) {
        return (
            <NavigationRedirect
                to={{
                    screen: {
                        type: 'home',
                        organization: screen.organization,
                    },
                    overlay: {
                        ...screen,
                    },
                }}
                replace
            />
        );
    }

    const navigateToSpace = (args: { space: api.Space; changeRequestId?: string }) => {
        navigate({
            screen: {
                type: 'space',
                space: args.space.id,
                organization: args.space.organization,
                page: '',
                query: undefined,
                changeRequest: args.changeRequestId,
            },
        });
    };

    return (
        <ImportContentDialog
            trigger="standalone"
            target={{ organization: screen.organization }}
            onStart={async (target) => {
                const siteId = screen.site;
                if (siteId) {
                    await toast.catch(async () => {
                        await apiClient.siteSpaces.addSpaceToSite(
                            target.space.organization,
                            siteId,
                            {
                                spaceId: target.space.id,
                            }
                        );
                    }, 'Error while adding space to the site');
                }
                navigateToSpace(target);
            }}
        />
    );
}
